import React, { createContext, PropsWithChildren, useEffect, useState, useContext } from 'react';

interface IBeforeInstallPromptEvent extends Event {
    readonly platforms: string[];
    readonly userChoice: Promise<{
        outcome: 'accepted' | 'dismissed';
        platform: string;
    }>;
    prompt(): Promise<void>;
}

type PromptCtx = {
    prompt: IBeforeInstallPromptEvent | null;
    promptToInstall: () => Promise<void> | null;
};

// 1. Create Context
export const A2HSContext = createContext<PromptCtx | null>(null);

// 2. Create custom hook
export const useA2HSContext = () => {
    const context = useContext(A2HSContext);

    if (!context) {
        throw new Error('useA2HSContext must be used within an A2HSProvider');
    }
    return context;
};

// 3. Create Provider
export const A2HSProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const [prompt, setState] = useState<IBeforeInstallPromptEvent | null>(null);

    const promptToInstall = () => {
        if (prompt) {
            return prompt.prompt();
        }
        return Promise.reject(new Error('Tried installing before browser sent "beforeinstallprompt" event'));
    };

    useEffect(() => {
        const ready = (e: IBeforeInstallPromptEvent) => {
            e.preventDefault();
            setState(e);
        };

        window.addEventListener('beforeinstallprompt', ready as any, false);

        return () => {
            window.removeEventListener('beforeinstallprompt', ready as any, false);
        };
    }, []);

    return <A2HSContext.Provider value={{ prompt, promptToInstall }}>{children}</A2HSContext.Provider>;
};
